// ./src/components/features/Connectors/ConnectorsTable.tsx
import React, { useMemo } from "react";
import { Text, Group } from "@mantine/core";

import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";
import { Connector } from "@/utils/api";
import { formatDateTime } from "@/utils/dateHelpers";
import { ConnectorCategoryBadge } from "../ConnectorCategoryBadge";
import { ConnectorTypeBadge } from "../ConnectorTypeBadge";

export interface ConnectorsTableProps {
  /** Array of connectors to display */
  connectors: Connector[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditConnector?: (connector: Connector) => void;

  /** Callback when remove action is clicked */
  onRemoveConnector?: (connector: Connector) => void;

  /** ID of connector currently being removed */
  removingConnectorId?: string | null;

  /** ID of connector just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ConnectorsTable: React.FC<ConnectorsTableProps> = ({
  connectors,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditConnector,
  onRemoveConnector,
  removingConnectorId,
  editedItemId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Connector>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 250,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Unnamed Connector"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "connectorCategory",
        label: "Category",
        sortable: true,
        width: 130,
        minWidth: 110,
        align: "left",
        render: (value) => <ConnectorCategoryBadge category={value} />,
      },
      {
        key: "connectorType",
        label: "Type",
        sortable: true,
        width: 180,
        minWidth: 150,
        align: "left",
        render: (value) => <ConnectorTypeBadge type={value} />,
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        width: 200,
        minWidth: 200,
        align: "left",
        render: (value) => (
          <Text
            size="sm"
            c="dimmed"
            dangerouslySetInnerHTML={{
              __html: formatDateTime(value, "datetime2"),
            }}
          ></Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 120,
        minWidth: 120,
        align: "left",
        render: (_, record: Connector) => {
          const isRemoving = removingConnectorId === record.id;

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <RecordActions
                context={record}
                onEditClick={onEditConnector}
                onRemoveClick={onRemoveConnector}
                removeInProgress={isRemoving}
              />
            </Group>
          );
        },
      },
    ],
    [onEditConnector, onRemoveConnector, removingConnectorId]
  );

  return (
    <AppTable
      data={connectors}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(connector) => connector?.id || ""}
      highlightRow={(connector) => connector?.id === editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
